import {
  describeOffer,
  isOfferVisible,
  offerDistanceKm,
  offerMarkerKm,
  offerOpacity,
  type OfferCatalog,
  type OfferFilters,
  type OfferOutcome,
  type PlottedOffer,
} from '@/entities/order';

/** One drawable courier offer: the offer plus everything the map derives from it. */
export interface OfferMarker {
  key: string;
  /** `[long, lat]` — the order ymaps3 and Leaflet helpers expect here. */
  coords: [number, number];
  offer: PlottedOffer;
  outcome: OfferOutcome;
  /** Straight-line km pickup → courier (detail card, "Расстояние"). */
  straightKm: number;
  /** km shown inside the marker: road distance when known, else straight-line. */
  badgeKm: number;
  opacity: 1 | 0.3;
  /** Currently being offered to this courier (live search push). */
  active: boolean;
}

/** Stable per-offer key: backend id, else the courier, else list position. */
export function offerKey(offer: PlottedOffer, index: number): string {
  return String(offer.id ?? `courier-${offer.courierId ?? index}-${index}`);
}

interface BuildMarkersInput {
  plotted: readonly PlottedOffer[];
  origin: { lat?: number | null; long?: number | null } | null | undefined;
  filters: OfferFilters;
  /** The reason table of the mode the offers came from. */
  catalog: OfferCatalog;
  /** Courier the live search is offering the order to right now, if any. */
  activeCourierId: string | number | null | undefined;
}

/** The markers to draw, in paint order (later = on top). */
export function buildOfferMarkers({
  plotted,
  origin,
  filters,
  catalog,
  activeCourierId,
}: BuildMarkersInput): OfferMarker[] {
  return plotted.flatMap((offer, index) => {
    if (!isOfferVisible(offer.facts, filters, catalog)) return [];
    const straightKm = offerDistanceKm(origin, offer);
    return [
      {
        key: offerKey(offer, index),
        coords: [offer.coords.long, offer.coords.lat] as [number, number],
        offer,
        outcome: describeOffer(offer.facts, catalog),
        straightKm,
        badgeKm: offerMarkerKm(offer, straightKm),
        opacity: offerOpacity(offer.facts, filters, catalog),
        active:
          activeCourierId != null &&
          offer.courierId != null &&
          String(activeCourierId) === String(offer.courierId),
      },
    ];
  });
}
